import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, MapPin, Clock, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobOffersSlider() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/job-offers?active_only=true`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching job offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 350;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">Caricamento offerte...</div>
        </div>
      </section>
    );
  }

  if (offers.length === 0) {
    return null;
  }

  return (
    <section data-testid="job-offers-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#E94560] mb-2 block">
              Opportunità
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E]">
              Nuove Offerte di Lavoro
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => scroll('left')}
                className="w-10 h-10 flex items-center justify-center bg-[#F5F5F7] hover:bg-[#1A1A2E] hover:text-white transition-colors"
                data-testid="job-slider-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-10 h-10 flex items-center justify-center bg-[#F5F5F7] hover:bg-[#1A1A2E] hover:text-white transition-colors"
                data-testid="job-slider-next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            <Link
              to="/offerte-lavoro"
              data-testid="view-all-jobs-btn"
              className="text-[#E94560] font-bold flex items-center gap-2 hover:underline"
            >
              Vedi tutte
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {/* Slider */}
        <div
          ref={sliderRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {offers.map((offer) => (
            <Link
              key={offer.id}
              to={`/offerte-lavoro/${offer.id}`}
              data-testid={`job-card-${offer.id}`}
              className="flex-shrink-0 w-[320px] bg-[#F5F5F7] border-2 border-transparent hover:border-[#1A1A2E] p-6 transition-all snap-start group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#27AE60] flex items-center justify-center">
                  <Briefcase className="text-white" size={24} />
                </div>
                <span className="text-xs font-bold bg-[#1A1A2E] text-white px-3 py-1">
                  {offer.contract_type}
                </span>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 group-hover:text-[#E94560] transition-colors line-clamp-2">
                {offer.title}
              </h3>
              {offer.company && (
                <p className="text-sm text-[#555555] mb-3">{offer.company}</p>
              )}
              <div className="flex flex-wrap gap-3 text-sm text-[#888888]">
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {offer.location}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {offer.experience_required}
                </span>
              </div>
              {offer.salary_range && (
                <p className="mt-3 text-sm font-bold text-[#27AE60]">{offer.salary_range}</p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
